import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";

import Board from "./index";
import styled, { createGlobalStyle } from "styled-components";
import { deleteBucket, deleteTask, saveBucket, updateTaskField } from "../api/ProjectApi";
import BucketAction from "../actions/BucketAction";
import TaskAction from "../actions/TaskAction";
import { loadBoard } from "./actions/BoardActions";
import { DialogTypes } from "../constants/action-types";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { fromJS, List } from "immutable";
import AmountField from "../components/AmountField";

const ExtraHeaderLane = styled.div<any>``;

const TooltipGlobalStyle = createGlobalStyle`
  .information-tooltip {
    padding: 0;
  }

  .react-tooltip-arrow {
    width: 0;
  }

  .crm-tooltip {
    z-index: 10000 !important;
    position: relative;
  }

  .crm-tooltip[style*="transparent"] {
    background-color: transparent !important;
    background: transparent !important;
  }

  .react-tooltip {
    z-index: 10000 !important;
    position: fixed !important;
    transform: none !important;
    transition: opacity 150ms ease-in-out !important;
  }

  .crm-tooltip {
    position: fixed !important;
    transform: none !important;
    transition: opacity 150ms ease-in-out !important;
    will-change: opacity !important;
  }

  .crm-tooltip[data-tooltip-content] {
    transform: none !important;
  }

  .crm-tooltip * {
    transform: none !important;
  }

  .crm-tooltip > div {
    overflow: visible !important;
  }
`;

type AddLanePropsType = {
  id: string;
  title: string;
};

interface UpdateBucketType {
  id?: string;
  name: string;
  order: number;
  isLoader?: boolean;
}

const BoardComponent = ({
  currentSize,
  contentType,
  projectView,
  onSearchTasks,
  loading: externalLoading,
}: any) => {
  const [deleteBucketId, setDeleteBucketId] = useState<string>("");

  const dispatch = useDispatch<any>();
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType));
  const tasks = useSelector((state: any) => state.taskReducer.getIn(["BOARD", "children"]));
  const pending = useSelector((state: any) => state.taskReducer.get("pending"));
  const users = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const groupBy = useSelector((state: any) => state.taskReducer.get("groupBy"));
  const groupByVisibleFieldIds =
    useSelector((state: any) => state.taskReducer.get("groupByVisibleFieldIds")) || fromJS([]);
  const groups = useSelector((state: any) => state.taskReducer.get(groupBy));
  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";

  // Detect stale data: tasks in Redux don't match the current contentType
  const firstTask = tasks?.first?.();
  const taskDataType = firstTask?.get?.("type");
  const hasStaleData = firstTask && taskDataType && taskDataType !== contentType;

  const isLoading = externalLoading || pending;

  const isBucket = groupBy === "bucketId";

  const getGroupName = () => {
    if (contentType == "SALE") {
      return "No Sale Funnel";
    }
    return "No Group";
  };

  const laneItems = () => {
    let groups;
    switch (groupBy) {
      case "assignees":
        const userGroups = users.map((assignee: any) => ({
          id: assignee.get("accountId"),
          name: assignee.get("displayName"),
          imageUrl: assignee.get("avatarUrl"),
        }));
        groups = [{ id: "", name: "Unassigned" }, ...userGroups];
        break;

      case "bucketId":
        groups =
          buckets.size > 0
            ? [{ id: "", name: "No stage" }, ...buckets.toJS()]
            : [{ id: "", name: getGroupName() }];
        break;
      default:
        groups = [];
    }

    return groups;
  };

  const groupTasks = (groupItemId: string) => {
    if (groupBy === "assignees") {
      return tasks.filter((task: any) => {
        const taskAssignees = task.getIn(["data", "assignees", "value"]) || fromJS([]);
        if (taskAssignees.size > 0) {
          return taskAssignees.toJS().includes(groupItemId);
        } else if (groupItemId == "") {
          return true;
        }
      });
    }
    if (groupBy === "bucketId") {
      return tasks.filter((task: any) => {
        const bucketValue = task.getIn(["data", "bucketId", "value"]) || "";
        return bucketValue == groupItemId;
      });
    }
    return groups.getIn([groupItemId]);
  };
  const laneWithData = laneItems().map((item: any) => {
    const { id, name, imageUrl, type = contentType } = item;
    const cards: any = [];
    if (groups) {
      const groupByItems = groupTasks(id);
      if (groupByItems) {
        groupByItems.map((task: any) => {
          cards.push({
            id: task.get("id"),
            title: task.getIn(["data", "name", "value"]),
            description: task.getIn(["data", "notes", "value"]),
            draggable: true,
            finishDate: task.getIn(["data", "finish", "value"]),
            task,
            users,
            isJira,
          });
        });
      }
    }
    return { id, title: name, type, imageUrl, color: item.color, cards };
  });

  const handleSaveBucket = async (props: UpdateBucketType) => {
    const { id, name, order, isLoader = true } = props;
    const newBucket = {
      id,
      projectId: project.get("id"),
      tenantId: "",
      name,
      order: order,
      type: contentType,
    };
    const bucketItem = await saveBucket(newBucket);
    if (bucketItem && isLoader) {
      dispatch(BucketAction.getBuckets(project.get("id"), contentType));
    }
  };

  const addLane = async (params: AddLanePropsType) => {
    await handleSaveBucket({ name: params.title, order: laneWithData.length });
  };

  const updateLane = async (id: string, params: any) => {
    const bucket = buckets.find((lane: any) => lane.get("id") === id);
    if (bucket)
      await handleSaveBucket({
        id: bucket.get("id"),
        name: params.title,
        order: bucket.get("order"),
      });
  };

  const deleteLane = async () => {
    if (deleteBucketId) {
      await deleteBucket(deleteBucketId, contentType);
      dispatch(BucketAction.getBuckets(project.get("id"), contentType));
      setDeleteBucketId("");
    }
  };

  const confirmDeleteBucket = (id: string) => {
    setDeleteBucketId(id);
  };

  const updateLaneOrder = async (removedIndex: number, addedIndex: number) => {
    const bucketRemove = buckets.toJS()[removedIndex];
    const bucketAdd = buckets.toJS()[addedIndex];
    if (bucketRemove && bucketAdd) {
      await handleSaveBucket({
        id: bucketRemove.id,
        name: bucketRemove.name,
        order: addedIndex,
        isLoader: false,
      });
      await handleSaveBucket({
        id: bucketAdd.id,
        name: bucketAdd.name,
        order: removedIndex,
      });
    }
  };

  const handleCreateItem = (item: any) => {
    dispatch(
      TaskAction.showTaskDialog(
        {
          projectId: project.get("id"),
          tenantId: project.get("tenantId"),
          type: contentType,
          projectView: projectView,
          data: {
            [groupBy]: { value: item.laneId },
          },
        },
        contentType
      )
    );
  };

  const handleDeleteTask = async (id: string, isLoader?: boolean) => {
    await deleteTask(id);
    if (isLoader) {
      await onSearchTasks({ projectId: project.get("id") });
    }
  };

  const onCardUpdate = async (laneId: string, updatedCard: any) => {
    const { fieldId, fieldValue, id } = updatedCard;
    if (id && fieldId) {
      await dispatch(TaskAction.updateTask(id, fieldId, fieldValue));
      onSearchTasks({ projectId: project.get("id") });
    } else {
      console.log("This func is not supported", laneId, updatedCard);
    }
  };

  const onCardDelete = async (id: string) => {
    await handleDeleteTask(id, true);
  };

  const calculateAssignees = (currentAssignee, newAssignee, task) => {
    let currentAssignees = task.getIn(["data", "assignees", "value"]) || fromJS([]);
    if (List.isList(currentAssignees)) {
      currentAssignees = currentAssignees.remove(currentAssignees.indexOf(currentAssignee));
      currentAssignees = currentAssignees.push(newAssignee);
      return currentAssignees.toJS();
    }
    return [newAssignee];
  };

  const onCardMoveAcrossLanes = async (
    oldLaneId: string,
    newLaneId: string,
    cardId: string,
    addedIndex: number,
    task: any
  ) => {
    if (oldLaneId !== newLaneId) {
      let value: any = newLaneId;
      if (groupBy == "assignees") {
        value = calculateAssignees(oldLaneId, newLaneId, task).join(",");
      }
      await updateTaskField(task.get("id"), groupBy, value);
      onSearchTasks({ projectId: task.get("projectId") });
    }
  };

  const onCardAddTask = (task: any) => {
    handleCreateItem(task);
  };

  const onShowTaskDialog = (task: any) => {
    dispatch(TaskAction.showRightPanelDialog(task, DialogTypes.sideDialog));
  };

  const renderConfirmDialog = () => {
    if (!buckets || !deleteBucketId) return;

    const deleteBucket = buckets.toJS().find((item: any) => item.id === deleteBucketId);
    if (!deleteBucket) return;

    return (
      <ConfirmDialog
        title={`Delete ${deleteBucket.name}`}
        description="All items in this group will also be deleted."
        visible={!!deleteBucketId}
        onClose={() => {
          setDeleteBucketId("");
        }}
        onSubmit={async () => {
          await deleteLane();
        }}
      />
    );
  };

  const renderDealValue = ({ cards = [] }: any) => {
    if (cards.length == 0) return null;
    if (contentType === "SALE") {
      let sumEstimatedRevenue = 0.0;

      cards.forEach((card: any) => {
        const dealValue = parseFloat(card?.task?.getIn(["data", "estimatedRevenue", "value"]));
        if (dealValue) {
          sumEstimatedRevenue += dealValue;
        }
      });

      return (
        <ExtraHeaderLane>
          <AmountField amount={sumEstimatedRevenue} />
        </ExtraHeaderLane>
      );
    }
    return null;
  };

  useEffect(() => {
    const hideColumns = groupByVisibleFieldIds.toJS();
    dispatch(
      loadBoard({
        lanes: laneWithData.filter((item) => !hideColumns.includes(item.id)),
      })
    );
  }, [buckets, projectView, taskFilters, tasks, laneWithData, dispatch, groupByVisibleFieldIds]);

  // Show skeleton when data is stale (mismatched contentType) or loading with no data
  const hasCards = laneWithData.some((lane: any) => lane.cards && lane.cards.length > 0);
  const showSkeleton = hasStaleData || (isLoading && !hasCards);

  return (
    <div className="w-full h-full">
      <TooltipGlobalStyle />
      {showSkeleton ? (
        <div className="flex gap-3 px-3 pt-3 h-full overflow-x-auto">
          {[3, 2, 1, 2].map((cardCount, laneIdx) => (
            <div key={laneIdx} className="flex-1 min-w-[220px] max-w-[280px]">
              {/* Lane header skeleton */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-200 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
                <div className="h-3 bg-gray-100 rounded w-5 animate-pulse" />
              </div>
              {/* Card skeletons */}
              <div className="space-y-2.5">
                {Array.from({ length: cardCount }).map((_, cardIdx) => (
                  <div
                    key={cardIdx}
                    className="bg-white rounded-lg border border-gray-200 p-3 animate-pulse"
                    style={{ animationDelay: `${laneIdx * 150 + cardIdx * 100}ms` }}
                  >
                    {/* Title */}
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                    {/* Field rows */}
                    {[65, 50, 35].map((width, rowIdx) => (
                      <div key={rowIdx} className="flex items-center gap-2 py-1">
                        <div className="w-3.5 h-3.5 bg-gray-100 rounded shrink-0" />
                        <div className="h-3 bg-gray-100 rounded" style={{ width: `${width}%` }} />
                      </div>
                    ))}
                    {/* Footer */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                      <div className="h-3 bg-gray-100 rounded w-16" />
                      <div className="h-3 bg-gray-100 rounded w-12" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Board
          currentSize={currentSize}
          buckets={buckets.toJS()}
          projectId={project.get("id")}
          data={{ lanes: laneWithData }}
          editLaneTitle={isBucket && contentType !== "SALE"}
          canAddLanes={isBucket}
          editable
          members={users}
          draggable
          isShowLaneCardForm={!isBucket}
          onCardClick={(task: any) => onShowTaskDialog(task.toJS())}
          draggableonLaneUpdate={updateLane}
          onLaneAdd={addLane}
          onLaneDelete={confirmDeleteBucket}
          onCardDelete={onCardDelete}
          onCardAdd={onCardAddTask}
          handleLaneDragEnd={updateLaneOrder}
          onCardMoveAcrossLanes={onCardMoveAcrossLanes}
          onCardUpdate={onCardUpdate}
          onLaneUpdate={updateLane}
          components
          extraHeaderLane={renderDealValue}
        />
      )}
      {renderConfirmDialog()}
    </div>
  );
};
export default BoardComponent;
